import { Stack, TextField, Typography } from '@mui/material';
import { useId, useMemo } from 'react';
import { Controller, type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { BackgroundImagePreview } from 'src/pages/Settings/memberships/components/background-image-preview';
import { ColorPickerField } from 'src/pages/Settings/memberships/components/color-picker-field';
import { DEFAULT_BRANDING_VALUES } from 'src/pages/Settings/memberships/components/plans-tab/form-values';
import { SectionWrapper } from 'src/pages/Settings/memberships/components/plans-tab/styled';
import {
  PlansTabField,
  type PlansTabFormValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';
import { isValidColor } from 'src/utils/color-utils';
import { isValidHttpUrl } from 'src/utils/url';

const COLOR_FIELDS: { name: FieldPath<PlansTabFormValues>; label: string }[] = [
  { name: PlansTabField.OptionsBrandingPrimaryColor, label: 'Primary color' },
  { name: PlansTabField.OptionsBrandingSecondaryColor, label: 'Secondary color' },
  { name: PlansTabField.OptionsBrandingBannerColor, label: 'Banner color' },
  { name: PlansTabField.OptionsBrandingPageBlock1Color, label: 'Page block 1 color' },
];

type StyleTabProps = { readOnly: boolean };

export const StyleTab = ({ readOnly }: StyleTabProps): React.JSX.Element => {
  const { control } = useFormContext<PlansTabFormValues>();
  const imageUrlId = useId();

  const backgroundImageUrl = useWatch({
    control,
    name: PlansTabField.OptionsBrandingBackgroundImageUrl,
  });

  const defaultColorByField = useMemo(
    () =>
      ({
        [PlansTabField.OptionsBrandingPrimaryColor]: DEFAULT_BRANDING_VALUES.primaryColor,
        [PlansTabField.OptionsBrandingSecondaryColor]: DEFAULT_BRANDING_VALUES.secondaryColor,
        [PlansTabField.OptionsBrandingBannerColor]: DEFAULT_BRANDING_VALUES.bannerColor,
        [PlansTabField.OptionsBrandingPageBlock1Color]:
          DEFAULT_BRANDING_VALUES.pageBlock1Color,
      }) as Record<FieldPath<PlansTabFormValues>, string>,
    [],
  );

  return (
    <Stack spacing={3} sx={{ px: 2, py: 2 }}>
      <SectionWrapper>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ fontSize: (t) => t.typography.pxToRem(14), color: 'text.primary', mb: 1 }}
        >
          Brand colors
        </Typography>

        <Stack spacing={0} sx={{ '& > *': { borderBottom: 1, borderColor: 'divider' } }}>
          {COLOR_FIELDS.map(({ name, label }) => (
            <Controller
              key={name}
              name={name}
              control={control}
              defaultValue={defaultColorByField[name]}
              rules={{
                validate: (value) => {
                  const str = typeof value === 'string' ? value : '';
                  const v = str.trim();
                  if (v === '') return 'Color is required';
                  if (!isValidColor(str)) return 'Enter a valid color (#nnn or #nnnnnn)';
                  return true;
                },
              }}
              render={({ field, fieldState }) => (
                <ColorPickerField
                  field={field}
                  label={label}
                  readOnly={readOnly}
                  hasError={Boolean(fieldState.error)}
                />
              )}
            />
          ))}
        </Stack>
      </SectionWrapper>

      <SectionWrapper>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ fontSize: (t) => t.typography.pxToRem(14), color: 'text.primary', mb: 1 }}
        >
          Background image
        </Typography>

        <Controller
          name={PlansTabField.OptionsBrandingBackgroundImageUrl}
          control={control}
          defaultValue=""
          rules={{
            validate: (value) => {
              const str = typeof value === 'string' ? value.trim() : '';
              if (str === '') return true;
              if (!isValidHttpUrl(str)) return 'Enter a valid URL (https://…)';
              return true;
            },
          }}
          render={({ field, fieldState }) => (
            <Stack spacing={1.5}>
              <Stack>
                <FormFieldLabel htmlFor={imageUrlId}>Image URL</FormFieldLabel>
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  disabled={readOnly}
                  size="small"
                  fullWidth
                  placeholder="https://example.com/background.jpg"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  id={imageUrlId}
                />
              </Stack>
              <BackgroundImagePreview
                url={typeof backgroundImageUrl === 'string' ? backgroundImageUrl : ''}
              />
            </Stack>
          )}
        />
      </SectionWrapper>
    </Stack>
  );
};
